/**
 * Container header reading (W1-14): channel count and sample rate of an encoded song without decoding it, for
 * songs that only play through the media element (longer than 20 minutes, or refused by decodeAudioData), where
 * no AudioBuffer exists to report them. Reads WAV (fmt chunk), FLAC (STREAMINFO), Ogg (Vorbis, Opus and FLAC
 * identification headers), MPEG audio (first frame header after an ID3v2 tag), AAC ADTS and MP4/M4A (audio sample
 * entry of the stsd box). Anything else, and any malformed header, reads as unknown (null fields).
 */

export interface ContainerInfo {
  /** Channel count from the header, or null when unknown. */
  readonly channels: number | null;
  /** Sample rate (Hz) of the stream, or null when unknown. Opus always decodes at 48 kHz. */
  readonly sampleRate: number | null;
}

const UNKNOWN: ContainerInfo = { channels: null, sampleRate: null };

/** MPEG audio frame headers are searched this far past the ID3v2 tag. */
const MPEG_SCAN_BYTES = 64 * 1024;
/** Box nesting depth followed in MP4 files (moov/trak/mdia/minf/stbl/stsd is 6). */
const MP4_MAX_DEPTH = 8;

const ADTS_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350] as const;
const MPEG1_RATES = [44100, 48000, 32000] as const;
const MP4_CONTAINERS = new Set(['moov', 'trak', 'mdia', 'minf', 'stbl']);
const MP4_AUDIO_ENTRIES = new Set(['mp4a', 'Opus', 'fLaC', 'alac', 'ac-3', 'ec-3', 'samr', 'sowt', 'twos', 'lpcm']);

function ascii(b: Uint8Array, at: number, n: number): string {
  if (at < 0 || at + n > b.length) return '';
  let s = '';
  for (let i = 0; i < n; i++) s += String.fromCharCode(b[at + i] ?? 0);
  return s;
}

function u8(b: Uint8Array, at: number): number {
  const v = b[at];
  if (v === undefined) throw new RangeError('header truncated');
  return v;
}

function u16le(b: Uint8Array, at: number): number {
  return u8(b, at) | (u8(b, at + 1) << 8);
}

function u32le(b: Uint8Array, at: number): number {
  return (u8(b, at) | (u8(b, at + 1) << 8) | (u8(b, at + 2) << 16) | (u8(b, at + 3) << 24)) >>> 0;
}

function u16be(b: Uint8Array, at: number): number {
  return (u8(b, at) << 8) | u8(b, at + 1);
}

function u32be(b: Uint8Array, at: number): number {
  return ((u8(b, at) << 24) | (u8(b, at + 1) << 16) | (u8(b, at + 2) << 8) | u8(b, at + 3)) >>> 0;
}

function info(channels: number, sampleRate: number): ContainerInfo {
  return {
    channels: Number.isInteger(channels) && channels >= 1 && channels <= 255 ? channels : null,
    sampleRate: Number.isFinite(sampleRate) && sampleRate >= 1000 && sampleRate <= 768000 ? sampleRate : null,
  };
}

function wav(b: Uint8Array): ContainerInfo {
  let at = 12;
  while (at + 8 <= b.length) {
    const id = ascii(b, at, 4);
    const size = u32le(b, at + 4);
    if (id === 'fmt ') return info(u16le(b, at + 10), u32le(b, at + 12));
    at += 8 + size + (size & 1);
  }
  return UNKNOWN;
}

/** STREAMINFO of a FLAC stream whose 'fLaC' marker is at `at`. */
function flacStreamInfo(b: Uint8Array, at: number): ContainerInfo {
  if (ascii(b, at, 4) !== 'fLaC' || (u8(b, at + 4) & 0x7f) !== 0) return UNKNOWN;
  const d = at + 8;
  const rate = (u8(b, d + 10) << 12) | (u8(b, d + 11) << 4) | (u8(b, d + 12) >> 4);
  const channels = ((u8(b, d + 12) >> 1) & 7) + 1;
  return info(channels, rate);
}

function ogg(b: Uint8Array): ContainerInfo {
  const segments = u8(b, 26);
  const p = 27 + segments;
  if (u8(b, p) === 1 && ascii(b, p + 1, 6) === 'vorbis') return info(u8(b, p + 11), u32le(b, p + 12));
  if (ascii(b, p, 8) === 'OpusHead') return info(u8(b, p + 9), 48000);
  if (u8(b, p) === 0x7f && ascii(b, p + 1, 4) === 'FLAC') return flacStreamInfo(b, p + 9);
  return UNKNOWN;
}

/** AAC ADTS frame header at `at`. */
function adts(b: Uint8Array, at: number): ContainerInfo {
  const b2 = u8(b, at + 2);
  const rate = ADTS_RATES[(b2 >> 2) & 0x0f];
  const config = ((b2 & 1) << 2) | (u8(b, at + 3) >> 6);
  return {
    channels: config === 0 ? null : config === 7 ? 8 : config,
    sampleRate: rate ?? null,
  };
}

function mpeg(b: Uint8Array): ContainerInfo {
  let at = 0;
  if (ascii(b, 0, 3) === 'ID3') {
    const size = ((u8(b, 6) & 0x7f) << 21) | ((u8(b, 7) & 0x7f) << 14) | ((u8(b, 8) & 0x7f) << 7) | (u8(b, 9) & 0x7f);
    at = 10 + size + ((u8(b, 5) & 0x10) !== 0 ? 10 : 0);
  }
  const end = Math.min(b.length - 4, at + MPEG_SCAN_BYTES);
  for (; at < end; at++) {
    if (b[at] !== 0xff) continue;
    const b1 = u8(b, at + 1);
    if ((b1 & 0xe0) !== 0xe0) continue;
    const version = (b1 >> 3) & 3; // 0 MPEG-2.5, 1 reserved, 2 MPEG-2, 3 MPEG-1
    const layer = (b1 >> 1) & 3; // 0 = AAC ADTS when the version bit pattern is MPEG-2/4
    if (layer === 0) {
      if ((b1 & 0xf6) === 0xf0) return adts(b, at);
      continue;
    }
    const b2 = u8(b, at + 2);
    const bitrateIndex = b2 >> 4;
    const rateIndex = (b2 >> 2) & 3;
    const base = MPEG1_RATES[rateIndex];
    // Reserved version, bad or free-format bitrate (rare, and the usual false sync on zero padding), bad rate.
    if (version === 1 || bitrateIndex === 0 || bitrateIndex === 15 || base === undefined) continue;
    const rate = version === 3 ? base : version === 2 ? base / 2 : base / 4;
    return info(u8(b, at + 3) >> 6 === 3 ? 1 : 2, rate);
  }
  return UNKNOWN;
}

/** The first audio sample entry inside the boxes in [start, end). */
function mp4Boxes(b: Uint8Array, start: number, end: number, depth: number): ContainerInfo | null {
  let at = start;
  while (at + 8 <= end) {
    let size = u32be(b, at);
    const type = ascii(b, at + 4, 4);
    let header = 8;
    if (size === 1) {
      // 64-bit size: songs are far below 2^53 bytes.
      size = u32be(b, at + 8) * 2 ** 32 + u32be(b, at + 12);
      header = 16;
    } else if (size === 0) {
      size = end - at;
    }
    if (size < header || at + size > end) return null;
    if (MP4_CONTAINERS.has(type) && depth < MP4_MAX_DEPTH) {
      const found = mp4Boxes(b, at + header, at + size, depth + 1);
      if (found !== null) return found;
    } else if (type === 'stsd') {
      // Full box: version/flags (4), entry_count (4), then sample entries.
      const found = mp4SampleEntries(b, at + header + 8, at + size);
      if (found !== null) return found;
    }
    at += size;
  }
  return null;
}

function mp4SampleEntries(b: Uint8Array, start: number, end: number): ContainerInfo | null {
  let at = start;
  while (at + 36 <= end) {
    const size = u32be(b, at);
    if (size < 8 || at + size > end) return null;
    if (MP4_AUDIO_ENTRIES.has(ascii(b, at + 4, 4))) {
      // SampleEntry: reserved (6), data_reference_index (2); AudioSampleEntry: reserved (8), channelcount (2),
      // samplesize (2), pre_defined (2), reserved (2), samplerate (16.16 fixed point).
      return info(u16be(b, at + 24), u32be(b, at + 32) / 65536);
    }
    at += size;
  }
  return null;
}

/** Channel count and sample rate from the container header (null fields when unknown). */
export function readContainerInfo(bytes: Uint8Array): ContainerInfo {
  try {
    const head = ascii(bytes, 0, 4);
    if (head === 'RIFF' && ascii(bytes, 8, 4) === 'WAVE') return wav(bytes);
    if (head === 'fLaC') return flacStreamInfo(bytes, 0);
    if (head === 'OggS') return ogg(bytes);
    if (ascii(bytes, 4, 4) === 'ftyp') return mp4Boxes(bytes, 0, bytes.length, 0) ?? UNKNOWN;
    if (ascii(bytes, 0, 3) === 'ID3' || bytes[0] === 0xff) return mpeg(bytes);
    return UNKNOWN;
  } catch {
    return UNKNOWN; // truncated or malformed header
  }
}

/** What an MP3's Xing / Info tag says about the samples a decoder should drop (gapless playback). */
export interface Mp3Gapless {
  /** "Xing" (VBR) or "Info" (CBR). */
  readonly tag: 'Xing' | 'Info';
  /** Total MPEG frames, or null when the tag does not say. */
  readonly frames: number | null;
  /** Encoder name from the LAME extension ("LAME3.100", "Lavc61.3"), or null. */
  readonly encoder: string | null;
  /** Encoder delay and end padding in samples from the LAME extension, or null without it. */
  readonly encoderDelay: number | null;
  readonly padding: number | null;
}

/**
 * Reads the Xing / Info tag of the first MPEG audio frame (after an ID3v2 tag). Browsers that honour it drop the
 * encoder delay (plus the decoder's 529 samples) at the start; a file without it starts about 25 ms late at
 * 44.1 kHz in every decoder (W3-07: MP3 decoder-delay check of the A/V lab, `#/dev/av`). Null when there is no tag.
 */
export function readMp3Gapless(bytes: Uint8Array): Mp3Gapless | null {
  let at = 0;
  if (ascii(bytes, 0, 3) === 'ID3' && bytes.length >= 10) {
    const size = ((bytes[6] ?? 0) << 21) | ((bytes[7] ?? 0) << 14) | ((bytes[8] ?? 0) << 7) | (bytes[9] ?? 0);
    at = 10 + size;
  }
  // The tag sits right after the side information of the first frame: search its first 200 bytes.
  const end = Math.min(bytes.length - 8, at + 200);
  let k = -1;
  for (let i = at; i < end; i++) {
    const s = ascii(bytes, i, 4);
    if (s === 'Xing' || s === 'Info') {
      k = i;
      break;
    }
  }
  if (k < 0) return null;
  const u32 = (p: number): number =>
    (((bytes[p] ?? 0) << 24) | ((bytes[p + 1] ?? 0) << 16) | ((bytes[p + 2] ?? 0) << 8) | (bytes[p + 3] ?? 0)) >>> 0;
  const flags = u32(k + 4);
  let p = k + 8;
  let frames: number | null = null;
  if ((flags & 1) !== 0) {
    frames = u32(p);
    p += 4;
  }
  if ((flags & 2) !== 0) p += 4;
  if ((flags & 4) !== 0) p += 100;
  if ((flags & 8) !== 0) p += 4;
  const enc = ascii(bytes, p, 9)
    .replace(/[^\x20-\x7e]/g, '')
    .trim();
  const hasLame = /^(LAME|Lavc|Lavf|L3\.99)/.test(enc) && p + 24 <= bytes.length;
  const d0 = bytes[p + 21] ?? 0;
  const d1 = bytes[p + 22] ?? 0;
  const d2 = bytes[p + 23] ?? 0;
  return {
    tag: ascii(bytes, k, 4) as 'Xing' | 'Info',
    frames,
    encoder: enc === '' ? null : enc,
    encoderDelay: hasLame ? (d0 << 4) | (d1 >> 4) : null,
    padding: hasLame ? ((d1 & 15) << 8) | d2 : null,
  };
}
